import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wallet, RefreshCw, Calendar, X,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableEmpty, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { formatDate, formatMXN, toISODate } from "@/lib/utils";
import { useListFacturas, type FacturasFilters } from "./api";
import type { FacturaSummary } from "./schemas";
import { ESTATUS_LABEL, ESTATUS_VARIANT } from "./helpers";
// rc139 — móvil: tarjetas
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

export function FacturasListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [estatus, setEstatus] = useState<string>("");
  const [desde, setDesde] = useState<string>("");
  const [hasta, setHasta] = useState<string>("");

  const filters: FacturasFilters = useMemo(
    () => ({
      page, size: 50,
      estatus: estatus || undefined,
      desde: desde || undefined,
      hasta: hasta || undefined,
    }),
    [page, estatus, desde, hasta],
  );

  const { data, isLoading, isFetching, refetch } = useListFacturas(filters);
  const items = data?.items ?? [];
  const totalPages = data?.pages ?? 1;

  const hasFilters = !!(estatus || desde || hasta);

  function resetFilters() {
    setEstatus("");
    setDesde("");
    setHasta("");
    setPage(1);
  }

  function setQuickRange(days: number) {
    const today = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDesde(toISODate(from));
    setHasta(toISODate(today));
    setPage(1);
  }

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Facturas</h2>
          <p className="text-xs text-muted-foreground">
            {data
              ? `${data.total} factura${data.total === 1 ? "" : "s"}`
              : "Cargando…"}
            {isFetching && !isLoading && " · actualizando"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw size={12} className={isFetching ? "animate-spin" : ""} /> Refrescar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      {/* rc139 móvil: estatus a lo ancho, fechas a la mitad, atajos en fila */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-card p-3 max-md:[&>div]:min-w-0 max-md:[&>div:nth-child(2)]:flex-1 max-md:[&>div:nth-child(3)]:flex-1 max-md:[&_select]:w-full max-md:[&_input]:w-full">
        <div className="flex flex-col gap-0.5 max-md:basis-full">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Estatus
          </label>
          <Select
            value={estatus}
            onChange={(e) => { setEstatus(e.target.value); setPage(1); }}
            className="w-36"
          >
            <option value="">Todos</option>
            <option value="pendiente">Pendiente</option>
            <option value="parcial">Parcial</option>
            <option value="pagada">Pagada</option>
            <option value="vencida">Vencida</option>
            <option value="anulada">Anulada</option>
          </Select>
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Desde
          </label>
          <Input
            type="date" className="h-8 w-36"
            value={desde}
            onChange={(e) => { setDesde(e.target.value); setPage(1); }}
          />
        </div>

        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Hasta
          </label>
          <Input
            type="date" className="h-8 w-36"
            value={hasta}
            onChange={(e) => { setHasta(e.target.value); setPage(1); }}
          />
        </div>

        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => setQuickRange(7)}>
            <Calendar size={12} /> 7d
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickRange(30)}>
            <Calendar size={12} /> 30d
          </Button>
          <Button size="sm" variant="outline" onClick={() => setQuickRange(90)}>
            <Calendar size={12} /> 90d
          </Button>
        </div>

        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={resetFilters}>
            <X size={12} /> Limpiar
          </Button>
        )}
      </div>

      {/* rc139 — móvil: tarjetas */}
      {isMobile ? (
        <>
          {isLoading && items.length === 0 ? (
            <MobileEmpty>Cargando…</MobileEmpty>
          ) : items.length === 0 ? (
            <MobileEmpty>{hasFilters ? "Sin coincidencias con los filtros." : "No hay facturas emitidas todavía."}</MobileEmpty>
          ) : (
            <MobileList>
              {items.map((f) => (
                <MobileCard
                  key={f.id}
                  to={`/facturas/${f.id}`}
                  title={f.cliente_nombre ?? "—"}
                  subtitle={<><span className="font-mono">{f.folio}</span>{f.contrato_codigo ? <> · <span className="font-mono">{f.contrato_codigo}</span></> : null}</>}
                  right={<Badge variant={ESTATUS_VARIANT[f.estatus]}>{ESTATUS_LABEL[f.estatus] ?? f.estatus}</Badge>}
                >
                  <MobileMeta label="Total" mono>{formatMXN(f.total)}</MobileMeta>
                  <MobileMeta label="Saldo" mono>
                    {Number(f.saldo) > 0 ? <span className="text-destructive">{formatMXN(f.saldo)}</span> : <span className="text-muted-foreground">{formatMXN(0)}</span>}
                  </MobileMeta>
                  <MobileMeta label="Emisión">{formatDate(f.fecha_emision)}</MobileMeta>
                  <MobileMeta label="Vence"><VencimientoCell fecha={f.fecha_vencimiento} estatus={f.estatus} /></MobileMeta>
                </MobileCard>
              ))}
            </MobileList>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-xs">
              <span className="text-muted-foreground">Pág {data?.page ?? 1}/{totalPages}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </>
      ) : (
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Folio</TableHead>
              <TableHead>Cliente / Contrato</TableHead>
              <TableHead>Emisión</TableHead>
              <TableHead>Vencimiento</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
              <TableHead>Estatus</TableHead>
              <TableHead>CFDI</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && items.length === 0 ? (
              <TableEmpty>Cargando…</TableEmpty>
            ) : items.length === 0 ? (
              <TableEmpty>
                <EmptyState
                  icon={Wallet}
                  title="Sin facturas"
                  description={
                    hasFilters
                      ? "No hay facturas que coincidan con los filtros."
                      : "No hay facturas emitidas todavía. Genera una desde el detalle de un contrato."
                  }
                />
              </TableEmpty>
            ) : (
              items.map((f: FacturaSummary) => (
                <TableRow
                  key={f.id}
                  className="cursor-pointer"
                  onClick={() => navigate(`/facturas/${f.id}`)}
                >
                  <TableCell className="font-mono text-xs">{f.folio}</TableCell>
                  {/* v1.5.0-rc11: cliente (siempre) + contrato (si cabe en la línea inferior).
                      Si la pantalla es muy estrecha priorizamos solo cliente. */}
                  <TableCell className="text-xs max-w-[260px]">
                    <div className="truncate font-medium" title={f.cliente_nombre ?? undefined}>
                      {f.cliente_nombre ?? "—"}
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono truncate">
                      {f.cliente_codigo ?? ""}
                      {f.contrato_codigo ? (
                        <span className="hidden sm:inline">
                          {f.cliente_codigo ? " · " : ""}
                          {f.contrato_codigo}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs">{formatDate(f.fecha_emision)}</TableCell>
                  <TableCell className="text-xs">
                    <VencimientoCell fecha={f.fecha_vencimiento} estatus={f.estatus} />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMXN(f.total)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {Number(f.saldo) > 0 ? (
                      <span className="text-destructive">{formatMXN(f.saldo)}</span>
                    ) : (
                      <span className="text-muted-foreground">{formatMXN(0)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={ESTATUS_VARIANT[f.estatus]}>
                      {ESTATUS_LABEL[f.estatus]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[10px] font-mono text-muted-foreground max-w-[120px] truncate">
                    {f.cfdi_uuid ?? "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">
              Página {data?.page ?? 1} de {totalPages}
            </span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button size="sm" variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

function VencimientoCell({ fecha, estatus }: { fecha: string; estatus: string }) {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const venc = new Date(fecha + "T00:00:00");
  const diffDays = Math.round((venc.getTime() - hoy.getTime()) / (24 * 60 * 60 * 1000));

  if (estatus === "anulada" || estatus === "pagada") {
    return <span>{formatDate(fecha)}</span>;
  }
  if (diffDays < 0) {
    return (
      <span className="text-destructive">
        {formatDate(fecha)}
        <span className="ml-1 text-[10px]">
          ({Math.abs(diffDays)}d atraso)
        </span>
      </span>
    );
  }
  if (diffDays <= 5) {
    return (
      <span className="text-amber-600 dark:text-amber-400">
        {formatDate(fecha)}
        <span className="ml-1 text-[10px]">
          (en {diffDays}d)
        </span>
      </span>
    );
  }
  return <span>{formatDate(fecha)}</span>;
}
